import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Layers } from "lucide-react";
import { cn } from "@/lib/utils";
import { get, post, toastError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { EmptyState } from "@/components/common/States";
import { noDate, noDateShort } from "./CapacityBits";

const STATUS = {
  aktiv: { label: "Aktiv", cls: "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300" },
  utlopt: { label: "Utløpt", cls: "bg-amber-500/10 text-amber-800 dark:text-amber-300" },
  konvertert: { label: "Kontraktsfestet", cls: "bg-muted text-muted-foreground" },
  frigitt: { label: "Frigitt", cls: "bg-muted text-muted-foreground" },
};

const Row = ({ b, canWrite, onConvert, onRelease, busy }) => {
  const navigate = useNavigate();
  const st = STATUS[b.status] || STATUS.aktiv;
  const active = b.status === "aktiv" || b.status === "utlopt";
  return (
    <tr className="border-t border-border/60 text-[12.5px] transition-colors duration-150 hover:bg-muted/40" data-testid={`capacity-block-row-${b.id}`}>
      <td className="px-3 py-2.5 align-top">
        <div className="font-medium leading-5">{b.title}</div>
        <div className="text-[11.5px] text-muted-foreground">
          {b.customer_name || "Intern"}{b.quote_number ? ` · ${b.quote_number}` : ""}{b.project_number ? ` · ${b.project_number}` : ""}
        </div>
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 align-top font-mono tabular text-muted-foreground">{noDateShort(b.start_date)} – {noDate(b.end_date)}</td>
      <td className="whitespace-nowrap px-3 py-2.5 text-right align-top font-mono tabular font-medium">{b.units}</td>
      <td className="whitespace-nowrap px-3 py-2.5 align-top">
        <Badge variant="secondary" className={cn("rounded-full px-2 py-0 text-[11px] font-medium shadow-none", st.cls)}>{st.label}</Badge>
        {b.kind === "soft" && b.expires_at && b.status === "aktiv" && <div className="mt-0.5 font-mono text-[11px] tabular text-muted-foreground">utløper {noDateShort(b.expires_at)}{b.probability != null ? ` · ${Math.round(b.probability * 100)} %` : ""}</div>}
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-right align-top">
        {b.kind === "hard" && b.project_id && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px]" onClick={() => navigate(`/prosjekter/${b.project_id}`)} data-testid={`capacity-block-open-project-button-${b.id}`}>Prosjekt</Button>
        )}
        {canWrite && active && b.kind === "soft" && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px]" onClick={() => onConvert(b)} disabled={busy} data-testid={`capacity-block-convert-button-${b.id}`}>Kontraktsfest</Button>
        )}
        {canWrite && active && (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[12px] text-muted-foreground hover:text-rose-600" onClick={() => onRelease(b)} disabled={busy} data-testid={`capacity-block-release-button-${b.id}`}>Frigi</Button>
        )}
      </td>
    </tr>
  );
};

const Table = ({ rows, testId, ...rest }) => (
  <div className="overflow-x-auto scrollbar-thin">
    <table className="w-full min-w-[640px]" data-testid={testId}>
      <thead>
        <tr className="text-left text-[11px] text-muted-foreground">
          <th className="px-3 py-1.5 font-medium">Hva</th>
          <th className="px-3 py-1.5 font-medium">Periode</th>
          <th className="px-3 py-1.5 text-right font-medium">Senger</th>
          <th className="px-3 py-1.5 font-medium">Status</th>
          <th className="px-3 py-1.5" />
        </tr>
      </thead>
      <tbody>{rows.map((b) => <Row key={b.id} b={b} {...rest} />)}</tbody>
    </table>
  </div>
);

/** Soft holds · Kontraktsfestet – the blocks the engine counts as soft exposure / hard load. */
export const BlocksPanel = ({ canWrite, className }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(null); // {kind: 'release'|'convert', block}
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["capacity-blocks"], queryFn: () => get("/capacity/blocks") });
  const soft = useMemo(() => (data || []).filter((b) => b.kind === "soft"), [data]);
  const hard = useMemo(() => (data || []).filter((b) => b.kind === "hard"), [data]);
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["capacity-blocks"] });
    qc.invalidateQueries({ queryKey: ["capacity-outlook"] });
    qc.invalidateQueries({ queryKey: ["planning"] });
  };
  const release = useMutation({
    mutationFn: (b) => post(`/capacity/blocks/${b.id}/release`),
    onSuccess: (b) => { toast.success("Kapasitet frigitt", { description: `${b.units} senger · ${b.title}` }); invalidate(); },
    onError: (e) => toastError(e, "Kunne ikke frigi"),
  });
  const convert = useMutation({
    mutationFn: (b) => post(`/capacity/blocks/${b.id}/convert`, {}),
    onSuccess: (b) => {
      toast.success("Soft hold kontraktsfestet", { description: `${b.units} senger · ${b.title}`, action: b.project_id ? { label: "Åpne prosjekt", onClick: () => navigate(`/prosjekter/${b.project_id}`) } : undefined });
      invalidate();
    },
    onError: (e) => toastError(e, "Kunne ikke kontraktsfeste"),
  });
  const busy = release.isPending || convert.isPending;
  const act = (kind) => (b) => setConfirm({ kind, block: b });
  const run = () => {
    if (!confirm) return;
    if (confirm.kind === "release") release.mutate(confirm.block);
    else convert.mutate(confirm.block);
    setConfirm(null);
  };

  return (
    <div className={className}>
      {isLoading && <div className="space-y-2" data-testid="capacity-blocks-loading"><Skeleton className="h-9 w-full" /><Skeleton className="h-9 w-full" /><Skeleton className="h-9 w-full" /></div>}
      {isError && (
        <div className="flex items-center justify-between rounded-lg bg-rose-500/[0.06] px-3 py-2 text-[12.5px] text-rose-700 dark:text-rose-300" data-testid="capacity-blocks-error-alert">
          Kunne ikke laste blokker. <Button size="sm" variant="ghost" onClick={() => refetch()} data-testid="capacity-blocks-retry-button">Prøv igjen</Button>
        </div>
      )}
      {data && (
        <Tabs defaultValue="soft" data-testid="capacity-blocks-tabs">
          <TabsList className="h-8 bg-muted/70 p-0.5">
            <TabsTrigger value="soft" className="h-7 px-3 text-[12px]" data-testid="capacity-blocks-tab-soft">Soft holds <span className="ml-1.5 font-mono tabular text-muted-foreground">{soft.filter((b) => b.status === "aktiv").length}</span></TabsTrigger>
            <TabsTrigger value="hard" className="h-7 px-3 text-[12px]" data-testid="capacity-blocks-tab-hard">Kontraktsfestet <span className="ml-1.5 font-mono tabular text-muted-foreground">{hard.filter((b) => b.status === "aktiv").length}</span></TabsTrigger>
          </TabsList>
          <TabsContent value="soft" className="mt-3">
            {soft.length ? <Table rows={soft} testId="capacity-soft-holds-table" canWrite={canWrite} onConvert={act("convert")} onRelease={act("release")} busy={busy} /> : <EmptyState icon={Layers} title="Ingen soft holds" description="Soft holds oppstår når du holder kapasitet for et tilbud." testId="capacity-soft-holds-empty" className="py-8" />}
          </TabsContent>
          <TabsContent value="hard" className="mt-3">
            {hard.length ? <Table rows={hard} testId="capacity-hard-reservations-table" canWrite={canWrite} onConvert={act("convert")} onRelease={act("release")} busy={busy} /> : <EmptyState icon={Layers} title="Ingen kontraktsfestede reservasjoner fra kapasitetsplaner" description="Kontraktsfestede allokeringer på prosjekter telles uansett som hard last." testId="capacity-hard-reservations-empty" className="py-8" />}
          </TabsContent>
        </Tabs>
      )}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="capacity-block-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.kind === "release" ? "Frigi kapasitet?" : "Kontraktsfest soft hold?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "release"
                ? `${confirm?.block?.units} senger · ${confirm?.block?.title}. ${confirm?.block?.kind === "hard" ? `Allokeringene kanselleres og modulene blir ledige igjen.${confirm?.block?.created_project ? " Prosjektet som ble opprettet fra planen fjernes hvis det fortsatt er tomt." : ""}` : "Soft hold-en fjernes fra eksponeringen."} Logges i audit.`
                : `${confirm?.block?.units} senger · ${confirm?.block?.title}. Motoren kjører sourcing på nytt med dagens flåte og låser modulene på et prosjekt. Logges i audit.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="capacity-block-confirm-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={run} data-testid="capacity-block-confirm-ok">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {confirm?.kind === "release" ? "Frigi" : "Kontraktsfest"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
